import React, { useState, useEffect } from 'react';
import { jobService } from '../services/jobService';
import { customerService } from '../services/customerService';
import { siteService } from '../services/siteService';
import { invoiceService } from '../services/invoiceService';
import CustomerModal from './CustomerModal';
import SiteModal from './SiteModal';

const STATUSES = ['Not Sent', 'Sent', 'Paid', 'Partially Paid', 'Overdue', 'Cancelled'];
const PAID_STATUSES = ['Paid', 'Partially Paid'];

const InvoiceModal = ({ invoice, defaultJobId, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    job_id:      '',
    status:      'Not Sent',
    due_date:    (() => { const d = new Date(); d.setDate(d.getDate() + 30); return d.toISOString().slice(0, 10); })(),
    date_paid:   '',
    amount_paid: '',
  });
  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [sites, setSites] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [selectedSite, setSelectedSite] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [allInvoices, setAllInvoices] = useState([]);
  const [existingInvoice, setExistingInvoice] = useState(null);
  const [confirmedDuplicate, setConfirmedDuplicate] = useState(false);
  const [showNewCustomerModal, setShowNewCustomerModal] = useState(false);
  const [showNewSiteModal, setShowNewSiteModal] = useState(false);

  const isEditing = !!invoice;

  useEffect(() => {
    jobService.getJobs().then(res => setJobs(res.data || [])).catch(() => {});
    customerService.getCustomers().then(res => setCustomers((res.data || []).slice().sort((a, b) => a.name.localeCompare(b.name)))).catch(() => {});
    siteService.getSites().then(res => setSites((res.data || []).slice().sort((a, b) => a.name.localeCompare(b.name)))).catch(() => {});
    if (!invoice) {
      invoiceService.getInvoices().then(res => setAllInvoices(res.data || [])).catch(() => {});
    }
  }, []);

  useEffect(() => {
    if (!invoice && defaultJobId && sites.length && jobs.length) {
      const matchingJob = jobs.find(j => String(j.id) === String(defaultJobId));
      if (matchingJob) {
        const matchingSite = sites.find(s => String(s.id) === String(matchingJob.site_id));
        if (matchingSite) {
          setSelectedCustomer(String(matchingSite.customer_id));
          setSelectedSite(String(matchingSite.id));
          setFormData(prev => ({ ...prev, job_id: String(defaultJobId) }));
        }
      }
    }
  }, [defaultJobId, sites, jobs, invoice]);

  useEffect(() => {
    if (!invoice && defaultJobId && allInvoices.length > 0) {
      const found = allInvoices.find(inv => String(inv.job_id) === String(defaultJobId));
      setExistingInvoice(found || null);
    }
  }, [defaultJobId, allInvoices, invoice]);

  useEffect(() => {
    if (invoice && sites.length && jobs.length) {
      const matchingJob = jobs.find(j => String(j.id) === String(invoice.job_id));
      if (matchingJob) {
        const matchingSite = sites.find(s => String(s.id) === String(matchingJob.site_id));
        if (matchingSite) {
          setSelectedCustomer(String(matchingSite.customer_id));
          setSelectedSite(String(matchingSite.id));
        }
      }
    }
  }, [invoice, sites, jobs]);

  useEffect(() => {
    if (invoice) {
      setFormData({
        job_id:      invoice.job_id      || '',
        status:      invoice.status      || 'Not Sent',
        due_date:    invoice.due_date    || '',
        date_paid:   invoice.date_paid   || '',
        amount_paid: invoice.amount_paid != null ? String(invoice.amount_paid) : '',
      });
    }
  }, [invoice]);

  const getJobTotal = (jobId) => {
    const job = jobs.find(j => String(j.id) === String(jobId));
    if (!job) return '';
    const net = parseFloat(job.value);
    if (isNaN(net)) return '';
    const rate = job.vat_rate ? parseFloat(job.vat_rate.rate) : 0;
    return (net * (1 + rate / 100)).toFixed(2);
  };

  const getRemainingBalance = (jobId) => {
    const total = parseFloat(getJobTotal(jobId));
    if (isNaN(total)) return null;
    const prevPaid = isEditing && invoice?.amount_paid != null ? parseFloat(invoice.amount_paid) : 0;
    return Math.max(0, total - prevPaid).toFixed(2);
  };

  const filteredSites = selectedCustomer
    ? sites.filter(s => String(s.customer_id) === selectedCustomer)
    : sites;

  const filteredJobs = selectedSite
    ? jobs.filter(j => String(j.site_id) === selectedSite)
    : selectedCustomer
      ? jobs.filter(j => filteredSites.some(s => String(s.id) === String(j.site_id)))
      : jobs;

  const handleCustomerChange = (e) => {
    setSelectedCustomer(e.target.value);
    setSelectedSite('');
    setFormData(prev => ({ ...prev, job_id: '' }));
    setExistingInvoice(null);
    setConfirmedDuplicate(false);
  };

  const handleSiteChange = (e) => {
    setSelectedSite(e.target.value);
    setFormData(prev => ({ ...prev, job_id: '' }));
    setExistingInvoice(null);
    setConfirmedDuplicate(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'job_id' && !isEditing) {
      const found = allInvoices.find(inv => String(inv.job_id) === String(value));
      setExistingInvoice(found || null);
      setConfirmedDuplicate(false);
    }
    // Warn before resetting paid fields when switching between or away from paid statuses
    const leavingPaidStatus = name === 'status' && PAID_STATUSES.includes(formData.status) && value !== formData.status && formData.date_paid;
    if (leavingPaidStatus) {
      setPendingStatus(value);
      return;
    }
    setFormData(prev => {
      const newStatus = name === 'status' ? value : prev.status;
      const isPaidStatus = PAID_STATUSES.includes(newStatus);
      const becomingPaid = name === 'status' && value === 'Paid' && prev.status !== 'Paid';
      const becomingPartiallyPaid = name === 'status' && value === 'Partially Paid' && prev.status !== 'Partially Paid';
      const today = new Date().toISOString().slice(0, 10);
      const remainingOnBecomingPaid = getRemainingBalance(prev.job_id) ?? getJobTotal(prev.job_id);
      return {
        ...prev,
        [name]: value,
        ...(!isPaidStatus ? { date_paid: '', amount_paid: '' } : {}),
        ...(becomingPaid ? { amount_paid: remainingOnBecomingPaid, date_paid: today } : {}),
        ...(becomingPartiallyPaid ? { amount_paid: '0.00', date_paid: today } : {}),
      };
    });
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const confirmStatusChange = () => {
    setFormData(prev => ({ ...prev, status: pendingStatus, date_paid: '', amount_paid: '' }));
    setErrors(prev => ({ ...prev, date_paid: '' }));
    setPendingStatus(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Client-side: date paid is required when status is Paid (not for Partially Paid)
    if (formData.status === 'Paid') {
      const d = formData.date_paid;
      if (!d || isNaN(new Date(d).getTime())) {
        setErrors({ date_paid: ['Please enter a valid date paid.'] });
        return;
      }
      if (new Date(d) > new Date()) {
        setErrors({ date_paid: ['Date paid cannot be in the future.'] });
        return;
      }
    }
    if (PAID_STATUSES.includes(formData.status)) {
      const amt = parseFloat(formData.amount_paid);
      if (isNaN(amt) || amt <= 0) {
        setErrors({ amount_paid: ['Amount paid must be greater than 0.'] });
        return;
      }
    }
    // Accumulate payments and auto-set status based on total paid vs invoice total
    const dataToSave = { ...formData };
    if (PAID_STATUSES.includes(formData.status)) {
      const previouslyPaid = isEditing && invoice?.amount_paid != null ? parseFloat(invoice.amount_paid) : 0;
      const newPayment = parseFloat(dataToSave.amount_paid || 0);
      const totalPaid = previouslyPaid + newPayment;
      dataToSave.amount_paid = totalPaid.toFixed(2);
      const total = parseFloat(getJobTotal(formData.job_id));
      if (!isNaN(total)) {
        dataToSave.status = totalPaid >= total ? 'Paid' : 'Partially Paid';
      }
    }
    setLoading(true);
    setErrors({});
    try {
      await onSave(dataToSave);
    } catch (error) {
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        setErrors({ general: error.response?.data?.message || 'An error occurred. Please try again.' });
      }
    }
    setLoading(false);
  };

  const inputClass = (field) =>
    `w-full px-4 py-3 border-2 rounded-lg text-base transition-all duration-300 focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100 dark:disabled:bg-gray-600 ${
      errors[field]
        ? 'border-red-500 focus:border-red-500'
        : 'border-gray-200 dark:border-gray-600 focus:border-blue-500 dark:focus:border-blue-400'
    }`;

  return (
    <>
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            {isEditing ? 'Edit Invoice' : 'New Invoice'}
          </h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-2xl font-semibold leading-none transition-colors">
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {errors.general && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-300">
              {errors.general}
            </div>
          )}

          {!isEditing && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg px-4 py-3 text-sm text-blue-700 dark:text-blue-300">
              The invoice number will be auto-generated on creation.
            </div>
          )}

          {isEditing && (
            <div className="bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg px-4 py-3">
              <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide font-semibold mb-0.5">Invoice Number</p>
              <p className="text-lg font-bold text-gray-900 dark:text-white">#{invoice.invoice_number}</p>
            </div>
          )}

          <div className="space-y-1">
            <label className="block text-sm font-semibold text-gray-900 dark:text-white">Customer {!isEditing && <span className="text-red-500">*</span>}</label>
            {isEditing ? (
              <div className="px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700/50 text-gray-900 dark:text-white text-base">
                {customers.find(c => String(c.id) === selectedCustomer)?.name || '—'}
              </div>
            ) : (
              <div className="flex gap-2">
                <select value={selectedCustomer} onChange={handleCustomerChange} className={inputClass('customer_id') + ' flex-1'}>
                  <option value="">Select a customer...</option>
                  {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <button
                  type="button"
                  onClick={() => setShowNewCustomerModal(true)}
                  className="px-3 py-2 text-sm font-semibold text-blue-600 dark:text-blue-400 border-2 border-blue-300 dark:border-blue-600 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors whitespace-nowrap"
                >+ New</button>
              </div>
            )}
          </div>

          <div className="space-y-1">
            <label className="block text-sm font-semibold text-gray-900 dark:text-white">Site {!isEditing && <span className="text-red-500">*</span>}</label>
            {isEditing ? (
              <div className="px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700/50 text-gray-900 dark:text-white text-base">
                {sites.find(s => String(s.id) === selectedSite)?.name || '—'}
              </div>
            ) : (
              <div className="flex gap-2">
                <select value={selectedSite} onChange={handleSiteChange} disabled={!selectedCustomer} className={inputClass('site_id') + ' flex-1'}>
                  <option value="">Select a site...</option>
                  {filteredSites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <button
                  type="button"
                  onClick={() => setShowNewSiteModal(true)}
                  disabled={!selectedCustomer}
                  className="px-3 py-2 text-sm font-semibold text-blue-600 dark:text-blue-400 border-2 border-blue-300 dark:border-blue-600 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
                >+ New</button>
              </div>
            )}
          </div>

          <div className="space-y-1">
            <label className="block text-sm font-semibold text-gray-900 dark:text-white">Job {!isEditing && <span className="text-red-500">*</span>}</label>
            {isEditing ? (
              <div className="px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700/50 text-gray-900 dark:text-white text-base">
                {jobs.find(j => String(j.id) === String(formData.job_id))?.title || '—'}
              </div>
            ) : (
              <>
                <select name="job_id" value={formData.job_id} onChange={handleChange} disabled={!selectedSite} className={inputClass('job_id')}>
                  <option value="">Select a job...</option>
                  {filteredJobs.map(j => (
                    <option key={j.id} value={j.id}>{j.title}</option>
                  ))}
                </select>
                {errors.job_id && <p className="text-red-500 dark:text-red-400 text-sm">{errors.job_id[0]}</p>}
              </>
            )}
          </div>

          <div className="space-y-1">
            <label className="block text-sm font-semibold text-gray-900 dark:text-white">Due Date</label>
            <input
              type="date"
              name="due_date"
              value={formData.due_date}
              onChange={handleChange}
              className={inputClass('due_date')}
            />
            {errors.due_date && <p className="text-red-500 dark:text-red-400 text-sm">{errors.due_date[0]}</p>}
          </div>

          {isEditing && (
            <div className="space-y-1">
              <label className="block text-sm font-semibold text-gray-900 dark:text-white">Status</label>
              <select name="status" value={formData.status} onChange={handleChange} className={inputClass('status')}>
                {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              {errors.status && <p className="text-red-500 dark:text-red-400 text-sm">{errors.status[0]}</p>}
            </div>
          )}

          {PAID_STATUSES.includes(formData.status) && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="block text-sm font-semibold text-gray-900 dark:text-white">Date Paid</label>
                <input
                  type="date"
                  name="date_paid"
                  value={formData.date_paid}
                  onChange={handleChange}
                  className={inputClass('date_paid')}
                />
                {errors.date_paid && <p className="text-red-500 dark:text-red-400 text-sm">{errors.date_paid[0]}</p>}
              </div>
              <div className="space-y-1">
                <label className="block text-sm font-semibold text-gray-900 dark:text-white">Amount Paid inc. VAT (£)</label>
                <input
                  type="number"
                  name="amount_paid"
                  value={formData.amount_paid}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  className={inputClass('amount_paid')}
                />
                {isEditing && invoice?.amount_paid != null && parseFloat(invoice.amount_paid) > 0 && formData.job_id && (
                  <p className="text-xs text-amber-600 dark:text-amber-400 font-medium mt-1">
                    Remaining balance (inc. VAT): £{getRemainingBalance(formData.job_id) ?? '–'}
                  </p>
                )}
                {errors.amount_paid && <p className="text-red-500 dark:text-red-400 text-sm">{errors.amount_paid[0]}</p>}
              </div>
            </div>
          )}

          {existingInvoice && !confirmedDuplicate && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-lg p-4 space-y-3">
              <div className="flex items-start gap-2">
                <svg className="w-5 h-5 text-amber-500 dark:text-amber-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                </svg>
                <div>
                  <p className="text-sm font-semibold text-amber-800 dark:text-amber-200">Invoice already exists for this job</p>
                  <p className="text-xs text-amber-700 dark:text-amber-300 mt-0.5">
                    Invoice{' '}
                    <a href="/invoices" target="_blank" rel="noopener noreferrer" className="font-bold underline hover:text-amber-900 dark:hover:text-amber-100">
                      #{existingInvoice.invoice_number}
                    </a>{' '}
                    was already raised for this job. Do you still want to create another?
                  </p>
                </div>
              </div>
              <div className="flex gap-2 pl-7">
                <button
                  type="button"
                  onClick={() => { setFormData(prev => ({ ...prev, job_id: '' })); setExistingInvoice(null); }}
                  className="flex-1 px-3 py-2 border border-amber-400 text-amber-700 dark:text-amber-300 rounded-lg text-sm font-semibold hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors"
                >
                  Choose different job
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmedDuplicate(true)}
                  className="flex-1 px-3 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm font-semibold transition-colors"
                >
                  Proceed anyway
                </button>
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-3 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={loading || (!!existingInvoice && !confirmedDuplicate)} className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg font-semibold hover:opacity-90 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed">
              {loading ? 'Saving...' : (isEditing ? 'Save Changes' : 'Create Invoice')}
            </button>
          </div>
        </form>
      </div>

      {pendingStatus && (
        <div className="absolute inset-0 z-10 flex items-center justify-center p-6 bg-black/40 rounded-xl backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-6 w-full max-w-sm">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Reset Payment Details?</h3>
            <p className="text-gray-600 dark:text-gray-300 mb-6 text-sm">
              Changing the status to <strong>{pendingStatus}</strong> will reset the date paid
              {PAID_STATUSES.includes(pendingStatus) ? ' and amount paid' : ' and amount paid values'}. Are you sure?
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setPendingStatus(null)}
                className="flex-1 px-4 py-2.5 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Keep Paid
              </button>
              <button
                type="button"
                onClick={confirmStatusChange}
                className="flex-1 px-4 py-2.5 bg-red-500 text-white rounded-lg font-semibold hover:bg-red-600 transition-colors"
              >
                Yes, Change Status
              </button>
            </div>
          </div>
        </div>
      )}
    </div>

    {showNewCustomerModal && (
      <CustomerModal
        onSave={async (data) => {
          const res = await customerService.createCustomer(data);
          const newCustomer = res.data;
          const updated = await customerService.getCustomers();
          setCustomers((updated.data || []).slice().sort((a, b) => a.name.localeCompare(b.name)));
          setSelectedCustomer(String(newCustomer.id));
          setSelectedSite('');
          setFormData(prev => ({ ...prev, job_id: '' }));
          setShowNewCustomerModal(false);
        }}
        onClose={() => setShowNewCustomerModal(false)}
      />
    )}
    {showNewSiteModal && (
      <SiteModal
        site={{ customer_id: selectedCustomer }}
        onSave={async (data) => {
          const res = await siteService.createSite(data);
          const newSite = res.data;
          const updated = await siteService.getSites();
          setSites((updated.data || []).slice().sort((a, b) => a.name.localeCompare(b.name)));
          setSelectedSite(String(newSite.id));
          setFormData(prev => ({ ...prev, job_id: '' }));
          setShowNewSiteModal(false);
        }}
        onClose={() => setShowNewSiteModal(false)}
      />
    )}
    </>
  );
};

export default InvoiceModal;
